import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Clock, Plane, FileText, DollarSign, AlertCircle, ChevronRight, Send, Calendar, ShieldCheck, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_hours: 0,
    travel_hours: 0,
    total_entries: 0,
  });
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [actionItems, setActionItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, entriesRes, expensesRes, actionsRes] = await Promise.all([
        axiosInstance.get('/stats'),
        axiosInstance.get('/time-entries?limit=50'),
        axiosInstance.get('/expenses?limit=20').catch(() => ({ data: { expenses: [], total: 0 } })),
        axiosInstance.get('/admin/action-items').catch(() => ({ data: null })),
      ]);

      setStats({
        total_hours: statsRes.data.total_hours || 0,
        travel_hours: statsRes.data.total_travel_hours || 0,
        total_entries: statsRes.data.total_entries || 0
      });

      const entries = entriesRes.data.entries || entriesRes.data || [];
      const activeEntries = entries.filter(entry => !entry.archived);
      setTimeEntries(activeEntries);

      const expenseData = expensesRes.data.expenses || expensesRes.data || [];
      setExpenses(expenseData);

      setActionItems(actionsRes.data);
    } catch (error) {
      console.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Processar dados para gráficos
  const getHoursByWeek = () => {
    const weekData = {};
    
    if (!timeEntries || timeEntries.length === 0) {
      return {
        labels: [t('noData')],
        datasets: [{
          label: t('hoursPerWeek'),
          data: [0],
          backgroundColor: 'rgba(61, 165, 165, 0.8)',
          borderColor: 'rgba(61, 165, 165, 1)',
          borderWidth: 2,
        }],
      };
    }
    
    timeEntries.forEach((entry) => {
      const date = new Date(String(entry.date).slice(0, 10) + 'T12:00:00');
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weekData[weekKey]) {
        weekData[weekKey] = 0;
      }
      weekData[weekKey] += parseFloat(entry.hours_worked || 0);
    });

    const sorted = Object.entries(weekData)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .slice(-8); // Last 8 weeks

    return {
      labels: sorted.map(([date]) => {
        const d = new Date(date + 'T12:00:00');
        return `${d.getDate()}/${d.getMonth() + 1}`;
      }),
      datasets: [{
        label: t('hoursPerWeek'),
        data: sorted.map(([, hours]) => hours),
        backgroundColor: 'rgba(61, 165, 165, 0.8)',
        borderColor: 'rgba(61, 165, 165, 1)',
        borderWidth: 2,
      }],
    };
  };

  const getHoursByProject = () => {
    const projectData = {};
    
    if (!timeEntries || timeEntries.length === 0) {
      return {
        labels: [t('noData')],
        datasets: [{
          label: t('hours'),
          data: [0],
          backgroundColor: ['rgba(61, 165, 165, 0.8)'],
        }],
      };
    }
    
    timeEntries.forEach((entry) => {
      const project = entry.project || t('noProject');
      if (!projectData[project]) {
        projectData[project] = 0;
      }
      projectData[project] += parseFloat(entry.hours_worked || 0);
    });

    const sorted = Object.entries(projectData)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 projects

    return {
      labels: sorted.map(([project]) => project),
      datasets: [{
        label: 'Horas',
        data: sorted.map(([, hours]) => hours),
        backgroundColor: [
          'rgba(61, 165, 165, 0.8)',
          'rgba(45, 133, 133, 0.8)',
          'rgba(13, 61, 61, 0.8)',
          'rgba(26, 86, 86, 0.8)',
          'rgba(82, 195, 195, 0.8)',
        ],
      }],
    };
  };

  const getHoursByEmployee = () => {
    const employeeData = {};
    
    if (!timeEntries || timeEntries.length === 0) {
      return {
        labels: [t('noData')],
        datasets: [{
          label: t('hoursWorked'),
          data: [0],
          backgroundColor: 'rgba(61, 165, 165, 0.8)',
          borderColor: 'rgba(61, 165, 165, 1)',
          borderWidth: 2,
        }],
      };
    }
    
    timeEntries.forEach((entry) => {
      const employee = entry.user_name || t('unknown');
      if (!employeeData[employee]) {
        employeeData[employee] = 0;
      }
      employeeData[employee] += parseFloat(entry.hours_worked || 0);
    });

    const sorted = Object.entries(employeeData)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 employees

    return {
      labels: sorted.map(([employee]) => employee),
      datasets: [{
        label: t('hoursWorked'),
        data: sorted.map(([, hours]) => hours),
        backgroundColor: 'rgba(61, 165, 165, 0.8)',
        borderColor: 'rgba(61, 165, 165, 1)',
        borderWidth: 2,
      }],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        labels: {
          color: '#374151',
        },
      },
    },
    scales: {
      y: {
        ticks: {
          color: '#6b7280',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.06)',
        },
      },
      x: {
        ticks: {
          color: '#6b7280',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#374151',
        },
      },
    },
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-800 text-lg">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card className="bg-gradient-to-r from-[#0d3d3d] to-[#1a5555] border-transparent shadow-lg">
        <CardContent className="py-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_dreamy-mcnulty-2/artifacts/39goexey_JPEG-afbeelding-4738-BFE8-D0-0.jpeg" 
                alt="Cardoso Bouw Logo"
                className="w-32 h-32 object-contain"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Cardoso Bouw en Onderhoud</h1>
              <p className="text-white/90 text-base leading-relaxed">
                {t('companyDescription')}
              </p>
              <p className="text-white/80 text-sm mt-2">
                <strong>{t('ourServices')}:</strong> {t('services')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">{t('dashboard')}</h2>
        <p className="text-gray-500 text-lg">{t('systemOverview')}</p>
      </div>

      {/* Acties Vereist — admin attention dashboard */}
      {actionItems && (() => {
        const items = [
          {
            count: actionItems.quotes_unsent?.count || 0,
            icon: <FileText className="w-5 h-5" />,
            label: 'Offertes nog niet verzonden',
            color: 'amber',
            path: '/quotes',
            preview: actionItems.quotes_unsent?.items || [],
            previewKey: 'number',
          },
          {
            count: actionItems.quotes_expiring?.count || 0,
            icon: <AlertTriangle className="w-5 h-5" />,
            label: 'Offertes verlopen ≤ 2 dagen',
            color: 'red',
            path: '/quotes',
            preview: actionItems.quotes_expiring?.items || [],
            previewKey: 'number',
          },
          {
            count: actionItems.invoices_due_tomorrow?.count || 0,
            icon: <FileSpreadsheet className="w-5 h-5" />,
            label: 'Facturen verlopen morgen',
            color: 'amber',
            path: '/invoices',
            preview: actionItems.invoices_due_tomorrow?.items || [],
            previewKey: 'number',
          },
          {
            count: actionItems.invoices_overdue?.count || 0,
            icon: <DollarSign className="w-5 h-5" />,
            label: 'Facturen achterstallig',
            color: 'red',
            path: '/invoices',
            preview: actionItems.invoices_overdue?.items || [],
            previewKey: 'number',
          },
          {
            count: actionItems.schedule_no_email_tomorrow?.count || 0,
            icon: <Calendar className="w-5 h-5" />,
            label: 'Agendamenten morgen zonder e-mail',
            color: 'blue',
            path: '/schedule',
            preview: actionItems.schedule_no_email_tomorrow?.items || [],
            previewKey: 'client_name',
          },
          {
            count: actionItems.warranties_unsent?.count || 0,
            icon: <ShieldCheck className="w-5 h-5" />,
            label: 'Garanties nog niet verzonden',
            color: 'amber',
            path: '/warranties',
            preview: actionItems.warranties_unsent?.items || [],
            previewKey: 'number',
          },
        ];
        const totalActions = items.reduce((s, it) => s + it.count, 0);
        if (totalActions === 0) {
          return (
            <Card className="bg-emerald-50 border-emerald-200" data-testid="action-items-card-empty">
              <CardContent className="py-5 flex items-center gap-3">
                <div className="bg-emerald-100 rounded-full p-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-emerald-900">Alles onder controle ✅</p>
                  <p className="text-sm text-emerald-700">Geen openstaande acties op dit moment.</p>
                </div>
              </CardContent>
            </Card>
          );
        }
        const colorMap = {
          amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconBg: 'bg-amber-100', iconText: 'text-amber-600' },
          red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', iconBg: 'bg-red-100', iconText: 'text-red-600' },
          blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', iconBg: 'bg-blue-100', iconText: 'text-blue-600' },
        };
        return (
          <Card className="bg-white border-2 border-amber-300 shadow-md" data-testid="action-items-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-gray-800">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Acties vereist
                <span className="text-xs font-normal text-gray-500">({totalActions} {totalActions === 1 ? 'actie' : 'acties'} openstaand)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.filter((it) => it.count > 0).map((it, idx) => {
                  const c = colorMap[it.color] || colorMap.amber;
                  return (
                    <button
                      key={idx}
                      onClick={() => navigate(it.path)}
                      className={`text-left ${c.bg} ${c.border} border rounded-lg p-3 hover:shadow-md transition-all group`}
                      data-testid={`action-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`${c.iconBg} ${c.iconText} rounded-md p-2 flex-shrink-0`}>
                          {it.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className={`text-2xl font-bold ${c.text}`}>{it.count}</span>
                            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 ml-auto" />
                          </div>
                          <p className={`text-xs font-medium ${c.text} mt-1`}>{it.label}</p>
                          {it.preview.length > 0 && (
                            <p className="text-[11px] text-gray-500 mt-1 truncate">
                              {it.preview.slice(0, 3).map((p) => p[it.previewKey]).filter(Boolean).join(' · ')}
                              {it.count > it.preview.length ? ' · ...' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('totalHours')}</CardTitle>
            <Clock className="w-4 h-4 text-[#3da5a5]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0d3d3d]">{(stats.total_hours || 0).toFixed(1)}h</div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('travelHours')}</CardTitle>
            <Plane className="w-4 h-4 text-[#3da5a5]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0d3d3d]">{(stats.travel_hours || 0).toFixed(1)}h</div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('totalEntries')}</CardTitle>
            <FileText className="w-4 h-4 text-[#3da5a5]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0d3d3d]">{stats.total_entries || 0}</div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">{t('totalExpenses')}</CardTitle>
            <DollarSign className="w-4 h-4 text-[#3da5a5]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0d3d3d]">€{(totalExpenses || 0).toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-800">📊 {t('hoursPerWeek')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={getHoursByWeek()} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-800">🏗️ {t('top5Projects')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <Doughnut data={getHoursByProject()} options={doughnutOptions} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-gray-800">👷 {t('hoursByEmployee')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Bar data={getHoursByEmployee()} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
